import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/report";
import { formatNumber } from "../../../utils/formatNumber";

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const [activityRows, setActivityRows] = useState([]);
  const [paymentRows, setPaymentRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getDailyPurchaseSummary(startDate, endDate);
        const data = res.data?.data || {};
        setActivityRows(data.activities || []);
        setPaymentRows(data.payments || []);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const sumActivity = activityRows.reduce(
    (acc, row) => ({
      count: acc.count + (row.count || 0),
      amount: acc.amount + (row.amount || 0),
      vat: acc.vat + (row.vat || 0),
      total: acc.total + (row.total || 0),
    }),
    { count: 0, amount: 0, vat: 0, total: 0 }
  );

  const totalPayment = paymentRows.reduce(
    (sum, row) => sum + (row.total || 0),
    0
  );

  const cellStyle = { border: "1px solid #ddd" };

  // ---------- CSV helpers ----------
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportActivities = () => {
    const dataset = [
      ...activityRows.map((r) => ({
        กิจกรรมการเปิดบิล: r.name ?? "",
        จำนวนBatch: Number(r.count ?? 0),
        ยอดก่อนรวมภาษี: Number(r.amount ?? 0).toFixed(2),
        ยอดภาษี: Number(r.vat ?? 0).toFixed(2),
        ยอดสุทธิ: Number(r.total ?? 0).toFixed(2),
      })),
      {
        กิจกรรมการเปิดบิล: "Batchทั้งหมด",
        จำนวนBatch: Number(sumActivity.count ?? 0),
        ยอดก่อนรวมภาษี: Number(sumActivity.amount ?? 0).toFixed(2),
        ยอดภาษี: Number(sumActivity.vat ?? 0).toFixed(2),
        ยอดสุทธิ: Number(sumActivity.total ?? 0).toFixed(2),
      },
    ];
    exportToCSV(
      dataset,
      `purchase_activities_${startDate}_to_${endDate}.csv`
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs />

      {/* ฟิลเตอร์วันที่ */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        my={2}
      >
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* ตารางกิจกรรม */}
          <Grid item xs={12} md={12} lg={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
                สรุปยอดซื้อประจำวัน
              </Typography>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContentCopyIcon />}
                onClick={handleExportActivities}
              >
                ข้อมูลหลัก
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ width: 1 }}>
              <Table
                sx={{
                  width: 1,
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell sx={cellStyle}>กิจกรรมการเปิดบิล</TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      จำนวนBatch
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดก่อนรวมภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดสุทธิ
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={cellStyle}>{row.name}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {row.count}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNumber(row.amount)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNumber(row.vat)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNumber(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                      Batchทั้งหมด
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellStyle, fontWeight: "bold" }}
                    >
                      {sumActivity.count}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellStyle, fontWeight: "bold" }}
                    >
                      {formatNumber(sumActivity.amount)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellStyle, fontWeight: "bold" }}
                    >
                      {formatNumber(sumActivity.vat)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellStyle, fontWeight: "bold" }}
                    >
                      {formatNumber(sumActivity.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* ตารางช่องทางชำระเงิน */}
          <Grid item xs={12} md={12} lg={6}>
            <Typography variant="h6" gutterBottom>
              สรุปช่องทางชำระเงิน
            </Typography>
            <TableContainer component={Paper} sx={{ width: 1 }}>
              <Table
                sx={{
                  width: 1,
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell sx={cellStyle}>ช่องทาง</TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      จำนวน
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={cellStyle}>{row.method}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNumber(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                      ทั้งหมด
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...cellStyle, fontWeight: "bold" }}
                    >
                      {formatNumber(totalPayment)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
